import { DEFAULT_GEMINI_LANGUAGE } from './openExternalAi';

const KIND_PREFIXES = {
  en: {
    search: 'Search',
    ask: 'Ask',
    place: 'Pin',
    decide: 'Decide',
    'set-goal': 'Goal',
    'add-text': 'Note',
    organize: 'Organize',
    apply: 'Apply',
  },
  ko: {
    search: '검색',
    ask: '질문',
    place: '핀',
    decide: '결정',
    'set-goal': '목표',
    'add-text': '메모',
    organize: '정리',
    apply: '적용',
  },
};

const MESSAGES = {
  en: {
    doNext: 'The next insight for your goal',
    expandDoNext: 'Expand search next',
    collapseDoNext: 'Collapse search next',
    refreshDoNext: 'Refresh search next',
    refreshDoNextTooltip: 'Add or remove pins/objects first, then refresh next steps',
    refreshDoNextStaleTooltip: 'Pins or objects changed — refresh to update next steps',
    stillMissing: 'Still missing',
    settledSoFar: 'Things done so far for your goal',
    settledEmpty: 'No step is settled yet.',
    settledEvidenceCount: (n) => `${n} pins`,
    settledGone: 'No longer on the board',
    settledFromBoard: 'From board progress',
    settledFromSolved: 'Marked solved',
    validateNext: 'Validate next',
    workingConclusion: 'Working conclusion',
    impactHigh: 'High impact if wrong',
    impactMedium: 'Medium impact if wrong',
    impactLow: 'Lower impact if wrong',
    markValidationDone: 'Mark validated',
    epistemicAssumption: 'Assumption',
    epistemicHypothesis: 'Hypothesis',
    epistemicFact: 'Fact',
    epistemicUnset: 'Set type',
    epistemicCycleAria: 'Cycle knowledge type: assumption, hypothesis, fact, or clear',
    epistemicTooltips: {
      unset:
        'Label this object’s claim. Click to set Assumption.',
      assumption:
        'Assumption — a premise you are taking as given for now. Click for Hypothesis.',
      hypothesis:
        'Hypothesis — a claim you want to test or falsify. Click for Fact.',
      fact:
        'Fact — established enough for this board (for now). Click to clear the label.',
    },
    organize: 'Organize',
    organizeConfirmTitle: 'Organize the board?',
    organizeConfirmMessage:
      'This regroups pins into new frames and may move their positions. Connecting lines and notes inside frames are kept. You can undo afterward.',
    organizeConfirmLabel: 'Organize',
    askGemini: 'Ask Gemini',
    search: 'Search',
    askPrefix: 'Ask',
    searchPrefix: 'Search',
    searchInApp: 'In app',
    searchGoogle: 'Google',
    askInApp: 'In app',
    chatModelMenuLabel: 'Choose AI model',
    chatModelFast: 'Fast',
    chatModelBetter: 'Better',
    chatModelPro: 'Pro',
    chatModelFastDetail: 'Quick answers to simple questions.',
    chatModelBetterDetail: 'Most questions about the board.',
    chatModelProDetail: 'Plans, comparisons, and outlines.',
    chatModelPaidOnly: 'Included with Hobby and above.',
    askOuterGemini: 'Gemini',
    askOuterProvider: (name) => name || 'Gemini',
    chatAskExternalTooltip: (name) =>
      `Send this question and selected objects to ${name || 'Gemini'}`,
    askProviderTitle: 'Ask with',
    askProviderDescription:
      'When you use an Ask action’s external button, open this AI with the question prefilled.',
    missingDone: 'Done',
    partiallyFilled: 'Partially filled',
    partiallyFilledPct: (n) => `Partially filled · ${n}%`,
    actionTried: 'Tried',
    clickToRetry: 'Click to try again',
    solved: 'Solved',
    solvedTooltip:
      'Mark this missing part done. AI will see this next time it reads the canvas.',
    unsolvedTooltip: 'Undo solved and mark this missing part open again',
    notInterested: 'Not interested',
    interestedAgain: 'Interested',
    notInterestedTooltip:
      'Mark this missing part as not interesting. AI will see this next time it reads the canvas.',
    interestedAgainTooltip: 'Undo not interested for this missing part',
    stillMissingPrompt: 'Still missing',
    pickNextStep: 'Pick next step',
    quickPlan: 'Quick plan',
    decidePlan: 'Decide',
    setGoalHint: 'This sets the current step — it does not change the board goal.',
    customAnswerPlaceholder: 'Or type your own answer…',
    applyCustomTitle: 'Saves your answer so the next steps can use it',
    applyDecisionTitle: 'Saves your answer so the next steps can use it',
    chooseDestination: 'Where should this run?',
    chooseDestinationShort: 'Choose where to run',
    selectThenApply: 'Click to continue',
    clickToContinue: 'Click to continue',
    planChooseHint: 'Choose a direction — click to continue',
    planDecideHint: 'Click a choice to save it on the board. Later steps will update from it.',
    planNextLabel: 'What happens next',
    planNextStep: (label) => (label ? `Next: ${label}` : 'Next step'),
    planYourChoice: (label) => `Your choice: ${label}`,
    planWillSearch: (query) => (query ? `Search “${query}”` : 'Search'),
    planWillAsk: (question) => (question ? `Ask “${question}”` : 'Ask'),
    planWillSetGoal: (goal) => (goal ? `Set current step to “${goal}”` : 'Set current step'),
    planWillOrganize: 'Organize the board',
    planWillDiscuss: (answer) => (answer ? `Save “${answer}” as a decision` : 'Save this decision'),
    planChooseDestinationHint: 'Choose In app or external above, then continue',
    planReadyHint: (action) => `Ready — click ${action || 'Apply'} to continue`,
    decideOpenedSearch: 'Searching…',
    decideOpenedAsk: 'Opening ask…',
    decideSetGoal: (goal) => (goal ? `Current step set to “${goal}”` : 'Current step updated'),
    decideOrganized: 'Organizing the board…',
    decideNoted: (answer) => (answer ? `Noted: “${answer}”` : 'Decision noted'),
    decideFollowUp: (label) => (label ? `Next in Do next: ${label}` : ''),
    closePlan: 'Close',
    noRecommendedStep: 'No recommended step aligned yet for this gap.',
    showsRecommendedSteps: (count) =>
      `Shows ${count} recommended step${count === 1 ? '' : 's'}`,
    willRunKind: (prefix) => `This will ${prefix}`,
    boardGoalPlaceholder: 'What is your goal?',
    boardGoalAria: 'Board goal',
    aboutBoardGoal: 'About board goal',
    boardGoalTooltip:
      'This is the board goal — the lasting purpose of the board. Mode (Explore / Research / Create / Plan) is detected automatically from it. Current step in Do next is a smaller step toward finishing this.',
    currentStepExplainLabel: 'Why this step first',
    selectCurrentStep: 'Choose a step',
    coverageMissing: 'Missing on board',
    coverageThin: 'Thin coverage',
    coverageCovered: 'Already covered',
    groundedByClusters: (labels) => `Near: ${labels}`,
    highlightOnCanvas: 'Show on canvas',
    goalProgressTitle: (rate) =>
      `Progress on this current step (toward the board goal): ${rate}%`,
    arrangeOnBoard: 'Arrange on board',
    languageSelectAria: 'Canvas language',
    viewModeAria: 'View mode',
    galleryViewAria: 'Gallery view',
    canvasViewAria: 'Canvas view',
    thinking: {
      context: [
        'Scanning your pins…',
        'Looking for gaps and next steps…',
        'Drafting suggestions…',
      ],
      arrange: [
        'Reading the board layout…',
        'Grouping related pins…',
        'Planning frames and positions…',
      ],
      command: [
        'Understanding your request…',
        'Reading the canvas…',
        'Planning layout changes…',
      ],
      chat: [
        'Reading the conversation…',
        'Reviewing board context…',
        'Composing a reply…',
      ],
      analyze: [
        'Reviewing your board…',
        "Spotting what's missing…",
        'Preparing suggestions…',
      ],
    },
    thinkingLive: {
      preparing: 'Preparing…',
      thinking: 'Thinking…',
      building: 'Building suggestions…',
    },
    objectProcessing: {
      enriching: 'Reading this object…',
      placing: 'Deciding where it belongs…',
      digesting: 'Adding it to the board…',
      analyzing: 'Checking if this fills a gap…',
      locating: 'Moving it into place…',
    },
    howToUseAria: 'How to complete a step',
    howToPinTitle: 'How to add your discovery to the whiteboard',
    howToPinClose: 'Close',
    howToPinStep1Title: 'Pin anything from the web',
    howToPinStep1Description:
      'Install the browser extension, then right-click and choose “Pin it to your Ai canvas” to save exactly what you need.',
    howToPinInstallExtension: 'Add save feature to your browser',
    howToPinStep2Title: 'Anything else (Paste it as a screenshot)',
    howToPinStep2Description:
      'On a Mac, capture a screenshot with ⌘⇧4, then paste it onto the canvas (⌘V). The same paste works for text and links. Right-click empty space to type a note, or pin from in-app chat.',
    emptyOnboardingTitle: 'Add your first object',
    emptyOnboardingBody:
      'Pin from the web, or paste a screenshot or anything else.',
    emptyOnboardingSeeHow: 'See how',
    emptyOnboardingDismiss: 'Dismiss',
    emptyOnboardingAria: 'How to add your first object',
    arrowBoundBoth: 'Connected for AI',
    textAboutObject: 'About this object',
    textAboutObjectDetail: (label) => (
      label ? `About “${label}”` : 'About this object'
    ),
    arrowBoundPartial: 'Partly connected',
    arrowBoundNone: 'Not connected',
    arrowBoundHint: 'Drag each end onto a pin, note, or frame (border/title) so AI can use this link.',
    arrowBoundDetail: (from, to) => `${from} → ${to}`,
    arrowBoundEnd: 'Connected',
    arrowUnboundEnd: 'Free',
    arrowStartLabelPlaceholder: 'Start label (e.g. depends on)',
    arrowEndLabelPlaceholder: 'End label (e.g. enables)',
    arrowStartLabelAria: 'Link start label',
    arrowEndLabelAria: 'Link end label',
    arrowLabelPlaceholder: 'Relationship (e.g. supports)',
    arrowLabelAria: 'Relationship label',
    arrowReverse: 'Reverse',
    arrowReverseAria: 'Reverse connection direction',
    canvasSettings: 'Canvas settings',
    hiddenStepsTitle: 'Hidden steps',
    hiddenStepsDescription:
      'Steps you completed or marked as not interested. Restore one to show it again, or delete it permanently. Select several to delete at once.',
    restoreHiddenStep: 'Restore',
    restoreHiddenStepFailed: 'Could not restore this step',
    deleteHiddenStep: 'Delete',
    deleteHiddenStepFailed: 'Could not delete this step',
    deleteSelectedHiddenSteps: count =>
      count === 1 ? 'Delete selected' : `Delete selected (${count})`,
    selectAllHiddenSteps: 'Select all',
    clearHiddenStepSelection: 'Clear selection',
    contextMemoryTitle: 'Board context',
    contextMemoryDescription:
      'Completed steps, not interested marks, constraints, and claims the AI remembers. Restore or turn off a row, or select several to delete.',
    contextMemoryEmpty: 'Nothing saved yet. Marks and claims will show up here.',
    contextMemoryNotInterestedTitle: 'Not interested',
    contextMemoryCompletedTitle: 'Completed steps',
    contextMemorySolvedTitle: 'Solved',
    contextMemoryValidatedTitle: 'Validated claims',
    contextMemoryConstraintsTitle: 'Constraints and claims',
    contextMemoryInactive: 'Off',
    contextMemoryToggleActive: 'Use this for AI',
    contextMemoryRestoreFailed: 'Could not update this item',
    contextMemoryDeleteFailed: 'Could not delete the selected items',
    constraintKindDecision: 'Decision',
    constraintKindConstraint: 'Constraint',
    constraintKindFact: 'Claim',
    constraintKindAssumption: 'Assumption',
    constraintKindOpen: 'Open',
    pinLedgerHoverTitle: 'On the board · removed if deleted',
    pinLedgerHoverMore: count => `+${count} more`,
    continueToProvider: (name) => `Continue to ${name || 'Gemini'}`,
    extensionRequiredForProvider: (name) =>
      `Install the Chrome extension to pin what you find. You can continue to ${name || 'Gemini'} without it.`,
    close: 'Close',
    cancel: 'Cancel',
    deleteCanvasTitle: 'Delete this canvas',
    deleteCanvasDescription:
      'Permanently delete this canvas and all of its pins, comments, AI chat, and board data. This cannot be undone.',
    deleteCanvasButton: 'Delete canvas…',
    deleteCanvasDefaultBlocked: 'The default board cannot be deleted.',
    deleteCanvasConfirmTitle: 'Delete canvas permanently?',
    deleteCanvasConfirmHint: name =>
      `Type #${name} to confirm. All pins and related data will be removed.`,
    deleteCanvasConfirmLabel: 'Canvas name',
    deleteCanvasForever: 'Delete forever',
    deletingCanvas: 'Deleting…',
    deleteCanvasFailed: 'Could not delete this canvas',
  },
  ko: {
    doNext: '다음 검색',
    expandDoNext: '다음 검색 펼치기',
    collapseDoNext: '다음 검색 접기',
    refreshDoNext: '다음 검색 새로고침',
    refreshDoNextTooltip: '핀/오브젝트를 추가하거나 삭제한 뒤 새로고침할 수 있습니다',
    refreshDoNextStaleTooltip: '핀/오브젝트가 바뀌었습니다 — 새로고침하면 다음 할 일이 갱신됩니다',
    stillMissing: '아직 부족한 점',
    settledSoFar: '지금까지 끝낸 것',
    settledEmpty: '아직 끝낸 것이 없습니다.',
    settledEvidenceCount: (n) => `핀 ${n}개`,
    settledGone: '보드에 더 이상 없음',
    settledFromBoard: '보드 진행으로 완료',
    settledFromSolved: '해결됨으로 표시',
    validateNext: '다음에 검증할 것',
    workingConclusion: '잠정 결론',
    impactHigh: '틀리면 영향 큼',
    impactMedium: '틀리면 영향 보통',
    impactLow: '틀리면 영향 작음',
    markValidationDone: '검증 완료',
    epistemicAssumption: '가정',
    epistemicHypothesis: '가설',
    epistemicFact: '사실',
    epistemicUnset: '유형 설정',
    epistemicCycleAria: '지식 유형 순환: 가정, 가설, 사실, 또는 지우기',
    epistemicTooltips: {
      unset:
        '이 객체의 주장을 분류합니다. 클릭하면 가정이 됩니다.',
      assumption:
        '가정 — 지금은 참으로 두고 진행하는 전제. 클릭하면 가설로 바뀝니다.',
      hypothesis:
        '가설 — 검증·반증하고 싶은 주장. 클릭하면 사실로 바뀝니다.',
      fact:
        '사실 — 이 보드에서는 충분히 확정된 내용(당분간). 클릭하면 라벨이 지워집니다.',
    },
    organize: '정리',
    organizeConfirmTitle: '보드를 정리할까요?',
    organizeConfirmMessage:
      '핀을 새 프레임으로 다시 묶고 위치를 옮길 수 있습니다. 프레임 안의 연결선과 메모는 유지됩니다. 이후 실행 취소할 수 있습니다.',
    organizeConfirmLabel: '정리',
    askGemini: 'Gemini에 질문',
    search: '검색',
    askPrefix: '질문',
    searchPrefix: '검색',
    searchInApp: '앱에서',
    searchGoogle: 'Google',
    askInApp: '앱에서',
    chatModelMenuLabel: 'AI 모델 선택',
    chatModelFast: '빠름',
    chatModelBetter: '고품질',
    chatModelPro: '프로',
    chatModelFastDetail: '간단한 질문에 빠르게 답합니다.',
    chatModelBetterDetail: '보드에 대한 대부분의 질문.',
    chatModelProDetail: '계획, 비교, 개요.',
    chatModelPaidOnly: 'Hobby 이상 요금제에서 사용할 수 있습니다.',
    askOuterGemini: 'Gemini',
    askOuterProvider: (name) => name || 'Gemini',
    chatAskExternalTooltip: (name) =>
      `이 질문과 선택한 객체를 ${name || 'Gemini'}로 보냅니다`,
    askProviderTitle: '질문할 AI',
    askProviderDescription:
      'Ask 액션의 외부 버튼을 누르면 선택한 AI가 질문과 함께 열립니다.',
    missingDone: '완료',
    partiallyFilled: '부분적으로 채워짐',
    partiallyFilledPct: (n) => `부분적으로 채워짐 · ${n}%`,
    actionTried: '시도함',
    clickToRetry: '다시 하려면 클릭',
    solved: '해결됨',
    solvedTooltip:
      '이 부족한 점을 완료로 표시합니다. AI가 다음에 캔버스를 읽을 때 반영됩니다.',
    unsolvedTooltip: '해결됨을 취소하고 이 부족한 점을 다시 엽니다',
    notInterested: '관심 없음',
    interestedAgain: '관심 있음',
    notInterestedTooltip:
      '이 부족한 점을 관심 없음으로 표시합니다. AI가 다음에 캔버스를 읽을 때 반영됩니다.',
    interestedAgainTooltip: '이 부족한 점의 관심 없음을 취소합니다',
    stillMissingPrompt: '아직 부족한 점',
    pickNextStep: '다음 단계 선택',
    quickPlan: '빠른 계획',
    decidePlan: '결정',
    setGoalHint: '현재 단계를 설정합니다. 보드 목표는 바뀌지 않습니다.',
    customAnswerPlaceholder: '직접 답변을 입력하세요…',
    applyCustomTitle: '답변을 저장합니다. 다음 단계에서 이 결정을 사용합니다',
    applyDecisionTitle: '답변을 저장합니다. 다음 단계에서 이 결정을 사용합니다',
    chooseDestination: '어디서 실행할까요?',
    chooseDestinationShort: '실행 위치 선택',
    selectThenApply: '클릭해서 계속',
    clickToContinue: '클릭해서 계속',
    planChooseHint: '방향을 고르세요 — 클릭하면 바로 진행됩니다',
    planDecideHint: '선택을 클릭하면 보드에 저장되고, 이후 단계가 그 결정에 맞춰 바뀝니다.',
    planNextLabel: '다음에 할 일',
    planNextStep: (label) => (label ? `다음: ${label}` : '다음 단계'),
    planYourChoice: (label) => `선택: ${label}`,
    planWillSearch: (query) => (query ? `“${query}” 검색` : '검색'),
    planWillAsk: (question) => (question ? `“${question}” 질문` : '질문'),
    planWillSetGoal: (goal) => (goal ? `현재 단계를 “${goal}”(으)로 설정` : '현재 단계 설정'),
    planWillOrganize: '보드 정리',
    planWillDiscuss: (answer) => (answer ? `“${answer}” 결정으로 저장` : '이 결정을 저장'),
    planChooseDestinationHint: '위에서 앱 또는 외부를 선택한 뒤 계속하세요',
    planReadyHint: (action) => `준비됨 — ${action || '적용'}을(를) 눌러 계속`,
    decideOpenedSearch: '검색 중…',
    decideOpenedAsk: '질문 여는 중…',
    decideSetGoal: (goal) => (goal ? `현재 단계: “${goal}”` : '현재 단계가 업데이트됨'),
    decideOrganized: '보드를 정리하는 중…',
    decideNoted: (answer) => (answer ? `기록됨: “${answer}”` : '결정이 기록됨'),
    decideFollowUp: (label) => (label ? `다음 할 일: ${label}` : ''),
    closePlan: '닫기',
    noRecommendedStep: '이 부족한 점에 맞는 추천 단계가 아직 없습니다.',
    showsRecommendedSteps: (count) => `추천 단계 ${count}개 표시`,
    willRunKind: (prefix) => `${prefix} 실행`,
    boardGoalPlaceholder: '이 보드는 무엇을 위한 건가요?',
    boardGoalAria: '보드 목표',
    aboutBoardGoal: '보드 목표 안내',
    boardGoalTooltip:
      '보드 목표입니다 — 보드의 전체 목적입니다. 모드(탐색/리서치/만들기/계획)는 이 목표에서 자동으로 감지됩니다. Do next의 현재 단계는 이를 완료하기 위한 더 작은 단계입니다.',
    currentStepExplainLabel: '이 단계를 먼저 하는 이유',
    selectCurrentStep: '단계 선택',
    coverageMissing: '보드에 없음',
    coverageThin: '커버리지 부족',
    coverageCovered: '이미 충분함',
    groundedByClusters: (labels) => `근처: ${labels}`,
    highlightOnCanvas: '캔버스에서 보기',
    goalProgressTitle: (rate) =>
      `현재 단계(보드 목표를 향한 단계) 진행률: ${rate}%`,
    arrangeOnBoard: '보드에 배치',
    languageSelectAria: '캔버스 언어',
    viewModeAria: '보기 모드',
    galleryViewAria: '갤러리 보기',
    canvasViewAria: '캔버스 보기',
    thinking: {
      context: [
        '핀을 살펴보는 중…',
        '빈틈과 다음 단계를 찾는 중…',
        '추천을 작성하는 중…',
      ],
      arrange: [
        '보드 레이아웃을 읽는 중…',
        '관련 핀을 묶는 중…',
        '프레임과 위치를 계획하는 중…',
      ],
      command: [
        '요청을 이해하는 중…',
        '캔버스를 읽는 중…',
        '레이아웃 변경을 계획하는 중…',
      ],
      chat: [
        '대화를 읽는 중…',
        '보드 맥락을 검토하는 중…',
        '답변을 작성하는 중…',
      ],
      analyze: [
        '보드를 검토하는 중…',
        '빠진 부분을 찾는 중…',
        '추천을 준비하는 중…',
      ],
    },
    thinkingLive: {
      preparing: '준비 중…',
      thinking: '생각 중…',
      building: '추천을 정리하는 중…',
    },
    objectProcessing: {
      enriching: '이 오브젝트를 읽는 중…',
      placing: '위치를 정하는 중…',
      digesting: '보드에 추가하는 중…',
      analyzing: '부족한 부분을 채우는지 확인하는 중…',
      locating: '알맞은 위치로 옮기는 중…',
    },
    howToUseAria: '단계를 완료하는 방법',
    howToPinTitle: '캔버스에 오브젝트를 추가해 단계를 완료하세요',
    howToPinClose: '닫기',
    howToPinStep1Title: '웹에서 무엇이든 핀하기',
    howToPinStep1Description:
      '브라우저 확장 프로그램을 설치한 뒤, 우클릭하고 “Pin it to your Ai canvas”를 선택하면 원하는 내용을 바로 저장할 수 있습니다.',
    howToPinInstallExtension: '브라우저에 스티커 기능을 추가하세요',
    howToPinStep2Title: '그 외 (스크린샷, 텍스트, 메모)',
    howToPinStep2Description:
      'Mac에서는 ⌘⇧4로 스크린샷을 찍은 뒤 캔버스에 붙여넣으세요(⌘V). 텍스트와 링크도 같은 방법으로 붙여넣을 수 있고, 빈 곳을 우클릭하면 메모를 입력하거나 채팅에서 핀할 수 있습니다.',
    emptyOnboardingTitle: '첫 오브젝트를 추가하세요',
    emptyOnboardingBody:
      '웹에서 핀하거나, 스크린샷이나 다른 것을 붙여넣으세요.',
    emptyOnboardingSeeHow: '방법 보기',
    emptyOnboardingDismiss: '닫기',
    emptyOnboardingAria: '첫 오브젝트를 추가하는 방법',
    arrowBoundBoth: 'AI용으로 연결됨',
    textAboutObject: '이 오브젝트에 대한 메모',
    textAboutObjectDetail: (label) => (
      label ? `“${label}”에 대한 메모` : '이 오브젝트에 대한 메모'
    ),
    arrowBoundPartial: '일부만 연결됨',
    arrowBoundNone: '연결되지 않음',
    arrowBoundHint: '양 끝을 핀·메모·프레임(테두리/제목)에 붙이면 AI가 이 연결을 사용합니다.',
    arrowBoundDetail: (from, to) => `${from} → ${to}`,
    arrowBoundEnd: '연결됨',
    arrowUnboundEnd: '미연결',
    arrowStartLabelPlaceholder: '시작 라벨 (예: 의존)',
    arrowEndLabelPlaceholder: '끝 라벨 (예: 가능하게)',
    arrowStartLabelAria: '연결 시작 라벨',
    arrowEndLabelAria: '연결 끝 라벨',
    arrowLabelPlaceholder: '관계 (예: 지지)',
    arrowLabelAria: '관계 라벨',
    arrowReverse: '방향 바꾸기',
    arrowReverseAria: '연결 방향 바꾸기',
    canvasSettings: '캔버스 설정',
    hiddenStepsTitle: '숨긴 단계',
    hiddenStepsDescription:
      '완료했거나 관심 없음으로 표시한 단계입니다. 복원하면 목록에 다시 표시되고, 삭제하면 영구히 제거됩니다. 여러 개를 선택해 한 번에 삭제할 수 있습니다.',
    restoreHiddenStep: '복원',
    restoreHiddenStepFailed: '단계를 복원하지 못했습니다',
    deleteHiddenStep: '삭제',
    deleteHiddenStepFailed: '단계를 삭제하지 못했습니다',
    deleteSelectedHiddenSteps: count =>
      count === 1 ? '선택 삭제' : `선택 삭제 (${count})`,
    selectAllHiddenSteps: '모두 선택',
    clearHiddenStepSelection: '선택 해제',
    contextMemoryTitle: '보드 컨텍스트',
    contextMemoryDescription:
      '완료한 단계, 관심 없음 표시, 제약, AI가 기억하는 주장입니다. 복원하거나 끄거나, 여러 개를 선택해 삭제할 수 있습니다.',
    contextMemoryEmpty: '아직 저장된 항목이 없습니다. 표시와 주장이 여기에 나타납니다.',
    contextMemoryNotInterestedTitle: '관심 없음',
    contextMemoryCompletedTitle: '완료한 단계',
    contextMemorySolvedTitle: '해결됨',
    contextMemoryValidatedTitle: '검증한 주장',
    contextMemoryConstraintsTitle: '제약과 주장',
    contextMemoryInactive: '끔',
    contextMemoryToggleActive: 'AI에 사용',
    contextMemoryRestoreFailed: '이 항목을 바꾸지 못했습니다',
    contextMemoryDeleteFailed: '선택한 항목을 삭제하지 못했습니다',
    constraintKindDecision: '결정',
    constraintKindConstraint: '제약',
    constraintKindFact: '주장',
    constraintKindAssumption: '가정',
    constraintKindOpen: '미정',
    pinLedgerHoverTitle: '보드에 반영됨 · 삭제하면 함께 제거됩니다',
    pinLedgerHoverMore: count => `외 ${count}개`,
    continueToProvider: (name) => `${name || 'Gemini'}로 계속`,
    extensionRequiredForProvider: (name) =>
      `찾은 내용을 핀하려면 Chrome 확장 프로그램을 설치하세요. 확장 프로그램 없이도 ${name || 'Gemini'}로 계속할 수 있습니다.`,
    close: '닫기',
    cancel: '취소',
    deleteCanvasTitle: '이 캔버스 삭제',
    deleteCanvasDescription:
      '이 캔버스와 모든 핀, 댓글, AI 채팅, 보드 데이터를 영구적으로 삭제합니다. 되돌릴 수 없습니다.',
    deleteCanvasButton: '캔버스 삭제…',
    deleteCanvasDefaultBlocked: '기본 보드는 삭제할 수 없습니다.',
    deleteCanvasConfirmTitle: '캔버스를 영구 삭제할까요?',
    deleteCanvasConfirmHint: name =>
      `확인하려면 #${name}을(를) 입력하세요. 모든 핀과 관련 데이터가 삭제됩니다.`,
    deleteCanvasConfirmLabel: '캔버스 이름',
    deleteCanvasForever: '영구 삭제',
    deletingCanvas: '삭제 중…',
    deleteCanvasFailed: '캔버스를 삭제하지 못했습니다',
  },
};

export function normalizeCanvasLocale(language) {
  const input = String(language || '').trim();
  if (MESSAGES[input]) return input;
  if (input.startsWith('zh')) return 'en';
  const base = input.split('-')[0];
  if (MESSAGES[base]) return base;
  return DEFAULT_GEMINI_LANGUAGE;
}

export function getCanvasMessages(language) {
  const locale = normalizeCanvasLocale(language);
  return MESSAGES[locale] || MESSAGES.en;
}

export function getKindPrefix(kind, language) {
  const locale = normalizeCanvasLocale(language);
  const table = KIND_PREFIXES[locale] || KIND_PREFIXES.en;
  return table[kind] || table.apply || KIND_PREFIXES.en.apply;
}
